import React, { useState } from 'react';
import { Book, Star, Clock, Gift, ArrowRight, Trophy, Target } from 'lucide-react';

export default function ReadingChallengeView({ books, onStartChallenge, onSelectBook, isLoading = false }) {
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [challengeType, setChallengeType] = useState('book_count'); // 'book_count' or 'page_count'
  const [targetCount, setTargetCount] = useState(5);
  const [challengeDuration, setChallengeDuration] = useState(30); // days
  const [rewardAmount, setRewardAmount] = useState(10.00);

  const handleBookToggle = (book) => {
    setSelectedBooks(prev => {
      const isSelected = prev.some(b => b.id === book.id);
      if (isSelected) {
        return prev.filter(b => b.id !== book.id);
      } else {
        return [...prev, book];
      }
    });
  };

  const handleStartChallenge = () => {
    const challengeData = {
      type: challengeType,
      targetCount,
      duration: challengeDuration,
      rewardAmount,
      selectedBooks: selectedBooks.length > 0 ? selectedBooks : null,
      name: challengeType === 'book_count' 
        ? `${targetCount} Books in ${challengeDuration} Days`
        : `${targetCount} Pages in ${challengeDuration} Days`,
      description: challengeType === 'book_count'
        ? `Read ${targetCount} books in ${challengeDuration} days to earn $${rewardAmount.toFixed(2)}`
        : `Read ${targetCount} pages in ${challengeDuration} days to earn $${rewardAmount.toFixed(2)}`
    };
    
    onStartChallenge(challengeData);
  };

  const getBookCover = (book) => {
    // Use cover_url from book data if available, otherwise create initials
    if (book.cover_url) {
      return (
        <img 
          src={book.cover_url} 
          alt={book.book_title || book.title}
          className="w-full h-full object-cover rounded-lg"
        />
      );
    }
    
    const title = book.book_title || book.title || 'Unknown';
    const initials = title.split(' ').slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase();
    
    return (
      <div className="w-full h-full bg-gradient-to-br from-azure-500 to-violet-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
        {initials}
      </div>
    );
  };

  const getEstimatedReward = () => {
    const baseReward = challengeType === 'book_count' ? targetCount * 2 : targetCount * 0.1;
    return Math.max(5, Math.min(50, baseReward));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-azure-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Trophy className="w-10 h-10 md:w-12 md:h-12 text-violet-500 animate-pulse mx-auto mb-4" />
          <p className="text-stone-600 text-sm md:text-base">Loading challenge options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-azure-50 p-3 md:p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-violet-500 to-azure-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Start Your Reading Challenge</h1>
          <p className="text-stone-600 text-base md:text-lg px-4 md:px-0">Choose your books and set your goal to earn rewards!</p>
        </div>

        {/* Challenge Configuration */}
        <div className="bg-white rounded-2xl p-4 md:p-6 mb-6 md:mb-8 shadow-sm border border-violet-200">
          <h2 className="text-lg md:text-xl font-bold text-slate-900 mb-4 md:mb-6 flex items-center gap-2">
            <Target className="w-5 h-5 text-violet-600" />
            Challenge Settings
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Challenge Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">Challenge Type</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 md:gap-3 p-3 md:p-4 border border-stone-200 rounded-lg cursor-pointer hover:bg-stone-50 min-h-[60px]">
                  <input
                    type="radio"
                    name="challengeType"
                    value="book_count"
                    checked={challengeType === 'book_count'}
                    onChange={(e) => setChallengeType(e.target.value)}
                    className="text-violet-600 w-4 h-4 md:w-5 md:h-5"
                  />
                  <Book className="w-5 h-5 text-violet-600" />
                  <div>
                    <div className="font-medium text-sm md:text-base">Books Challenge</div>
                    <div className="text-xs md:text-sm text-stone-500">Count completed books</div>
                  </div>
                </label>
                <label className="flex items-center gap-2 md:gap-3 p-3 md:p-4 border border-stone-200 rounded-lg cursor-pointer hover:bg-stone-50 min-h-[60px]">
                  <input
                    type="radio"
                    name="challengeType"
                    value="page_count"
                    checked={challengeType === 'page_count'}
                    onChange={(e) => setChallengeType(e.target.value)}
                    className="text-violet-600 w-4 h-4 md:w-5 md:h-5"
                  />
                  <Star className="w-5 h-5 text-violet-600" />
                  <div>
                    <div className="font-medium text-sm md:text-base">Pages Challenge</div>
                    <div className="text-xs md:text-sm text-stone-500">Count pages read</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Target & Duration */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Target {challengeType === 'book_count' ? 'Books' : 'Pages'}
                </label>
                <input
                  type="number"
                  value={targetCount}
                  onChange={(e) => setTargetCount(parseInt(e.target.value) || 1)}
                  min="1"
                  max={challengeType === 'book_count' ? 20 : 1000}
                  className="w-full px-3 py-3 md:py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Duration (days)</label>
                <select
                  value={challengeDuration}
                  onChange={(e) => setChallengeDuration(parseInt(e.target.value))}
                  className="w-full px-3 py-3 md:py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                >
                  <option value={7}>1 Week</option>
                  <option value={14}>2 Weeks</option>
                  <option value={30}>1 Month</option>
                  <option value={60}>2 Months</option>
                  <option value={90}>3 Months</option>
                </select>
              </div>
            </div>
          </div>

          {/* Reward Preview */}
          <div className="mt-4 md:mt-6 p-4 bg-gradient-to-r from-violet-100 to-azure-100 rounded-xl">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <Gift className="w-5 h-5 md:w-6 md:h-6 text-violet-600" />
                <div>
                  <div className="font-semibold text-slate-900 text-sm md:text-base">Challenge Reward</div>
                  <div className="text-xs md:text-sm text-violet-700">Complete your goal to earn</div>
                </div>
              </div>
              <div className="text-xl md:text-2xl font-bold text-violet-600">
                ${getEstimatedReward().toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        {/* Book Selection */}
        <div className="bg-white rounded-2xl p-4 md:p-6 mb-6 md:mb-8 shadow-sm border border-violet-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6 gap-2">
            <h2 className="text-lg md:text-xl font-bold text-slate-900 flex items-center gap-2">
              <Book className="w-5 h-5 text-violet-600" />
              Choose Your Books
            </h2>
            <div className="text-sm text-slate-600">
              {selectedBooks.length} selected
            </div>
          </div>

          {books && books.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4 mb-6">
              {books.map((book) => {
                const isSelected = selectedBooks.some(b => b.id === book.id);
                return (
                  <div
                    key={book.id}
                    onClick={() => handleBookToggle(book)}
                    className={`relative cursor-pointer transition-all duration-200 min-h-[44px] ${
                      isSelected 
                        ? 'transform scale-105 ring-2 md:ring-4 ring-violet-500 ring-opacity-50' 
                        : 'hover:transform hover:scale-105'
                    }`}
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-md">
                      {getBookCover(book)}
                    </div>
                    
                    {/* Selection Overlay */}
                    {isSelected && (
                      <div className="absolute inset-0 bg-violet-600 bg-opacity-20 rounded-lg flex items-center justify-center">
                        <div className="w-8 h-8 bg-violet-600 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}
                    
                    {/* Book Info */}
                    <div className="mt-2 md:mt-3 text-center">
                      <h3 className="text-xs md:text-sm font-medium text-slate-900 truncate" title={book.book_title || book.title}>
                        {book.book_title || book.title}
                      </h3>
                      <p className="text-xs text-slate-600 truncate hidden sm:block" title={book.book_author || book.author}>
                        {book.book_author || book.author}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 md:py-12">
              <Book className="w-12 h-12 md:w-16 md:h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-2">No Books Available</h3>
              <p className="text-sm md:text-base text-slate-600 px-4">Add some books to your library to start a challenge!</p>
            </div>
          )}

          {books && books.length > 0 && (
            <div className="text-center">
              <p className="text-xs md:text-sm text-slate-600 mb-4 px-4 md:px-0">
                Select specific books for your challenge, or leave unselected to choose as you go
              </p>
            </div>
          )}
        </div>

        {/* Start Challenge Button */}
        <div className="text-center px-4 md:px-0">
          <button
            onClick={handleStartChallenge}
            disabled={!targetCount || targetCount < 1}
            className="inline-flex items-center justify-center gap-2 md:gap-3 px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-violet-600 to-azure-600 text-white font-bold text-base md:text-lg rounded-2xl hover:from-violet-700 hover:to-azure-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl min-h-[56px] w-full max-w-md mx-auto"
          >
            <Trophy className="w-5 h-5 md:w-6 md:h-6" />
            Start My Challenge
            <ArrowRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          
          <p className="text-xs md:text-sm text-slate-600 mt-4 px-2">
            Challenge goal: {challengeType === 'book_count' ? `${targetCount} books` : `${targetCount} pages`} in {challengeDuration} days
          </p>
        </div>
      </div>
    </div>
  );
}